import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, Upload, FileText, X } from 'lucide-react';
import type { AccountPayable, InvoiceFormData } from '@/types/accounts-payable.types';

interface InvoiceEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountPayable | null;
  onSubmit: (accountId: string, invoiceData: InvoiceFormData) => Promise<void>;
}

export const InvoiceEntryModal: React.FC<InvoiceEntryModalProps> = ({
  isOpen,
  onClose,
  account,
  onSubmit
}) => {
  const [formData, setFormData] = useState<InvoiceFormData>({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    amount: account?.originalAmount || 0,
    dueDate: '',
    notes: '',
    file: undefined
  });
  
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  React.useEffect(() => {
    if (account && isOpen) {
      setFormData({
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        amount: account.receivedAmount,
        dueDate: account.dueDate || '',
        notes: '',
        file: undefined
      });
    }
  }, [account, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;

    try {
      setLoading(true);
      await onSubmit(account.id, formData);
      onClose();
    } catch (error) {
      console.error('Error submitting invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const removeFile = () => {
    setFormData(prev => ({ ...prev, file: undefined }));
  };

  if (!account) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Ingresar Factura
          </DialogTitle>
          <div className="text-sm text-gray-600">
            Cuenta: {account.accountNumber} - {account.supplierName}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Info Card */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">OC:</span>
                  <span className="ml-2">{account.purchaseOrderNumber}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Monto Original:</span>
                  <span className="ml-2">S/ {account.originalAmount.toFixed(2)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Monto Recibido:</span>
                  <span className={`ml-2 font-semibold ${
                    account.receivedAmount !== account.originalAmount 
                      ? 'text-amber-600' 
                      : 'text-gray-900'
                  }`}>
                    S/ {account.receivedAmount.toFixed(2)}
                  </span>
                </div>
                {account.receivedAmount !== account.originalAmount && (
                  <div className="col-span-2">
                    <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      Diferencia: S/ {(account.receivedAmount - account.originalAmount).toFixed(2)}
                      {account.receivedAmount > account.originalAmount ? ' (recibido de más)' : ' (recibido de menos)'}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Invoice Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="invoiceNumber">
                Número de Factura *
              </Label>
              <Input
                id="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                placeholder="F001-00000123"
                required
              />
            </div>

            <div>
              <Label htmlFor="invoiceDate">
                Fecha de Factura *
              </Label>
              <Input
                id="invoiceDate"
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="amount">
                Monto de Factura *
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
                required
              />
              {formData.amount !== account.receivedAmount && (
                <div className="flex items-center gap-1 mt-1 text-amber-600 text-xs">
                  <AlertCircle className="w-3 h-3" />
                  El monto difiere del monto recibido
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="dueDate">
                Fecha de Vencimiento
              </Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>
          </div>

          {/* File Upload */}
          <div>
            <Label>Adjuntar Factura (Opcional)</Label>
            <div 
              className={`mt-2 border-2 border-dashed rounded-lg p-6 transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {formData.file ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium">{formData.file.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(formData.file.size / 1024 / 1024).toFixed(1)} MB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-sm text-gray-600 mb-1">
                    Arrastra y suelta tu factura aquí, o{' '}
                    <label className="text-blue-600 hover:text-blue-700 cursor-pointer underline">
                      selecciona archivo
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                  <div className="text-xs text-gray-500">
                    PDF, PNG, JPG hasta 10MB
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">
              Observaciones
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Observaciones adicionales sobre la factura..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="min-w-24"
            >
              {loading ? 'Guardando...' : 'Ingresar Factura'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};